import {
  Box,
  Button,
  Checkbox,
  Divider,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";

import { BankLogo } from "@dfp/react-components";

import type { BankFlowProps } from "./bankFlow.types.ts";

// Replica of Česká spořitelna's (George) PSD2 pages (see Confluence "Screen
// flow when connecting to a bank" → ČSAS). The copy is intentionally
// hard-coded Czech — these are external bank pages, not localized app UI.
const CSAS_BG = "#2B4A5C"; // dark teal full-viewport background
const CSAS_BLUE = "#2870ED"; // accent / links
const CSAS_BUTTON = "#1D70B7"; // deep blue primary buttons
const CSAS_TEXT = "#33373E";
const CSAS_WARNING_BG = "#FFF4D6";
const CSAS_WARNING_BORDER = "#F2D27A";

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

// ČSAS login has more screens than the coarse BankFlowStep union exposes, so
// the sub-screens are driven by a local stage union.
type CsasStage = "credentials" | "birth" | "george" | "consent";

const MONTHS = [
  "leden",
  "únor",
  "březen",
  "duben",
  "květen",
  "červen",
  "červenec",
  "srpen",
  "září",
  "říjen",
  "listopad",
  "prosinec",
];

function CsasWordmark() {
  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 1.5 }}>
      <BankLogo
        bank="CSAS"
        style={{ height: 34, width: "auto", display: "block" }}
      />
    </Stack>
  );
}

/** Small "{thirdPartyName}  EN" lockup shown on the login sub-screens. */
function CsasThirdPartyHeader({ thirdPartyName }: { thirdPartyName: string }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 17,
            color: CSAS_TEXT,
            fontFamily: FONT_STACK,
          }}
        >
          {thirdPartyName}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 12,
            color: "#fff",
            bgcolor: CSAS_BLUE,
            borderRadius: 0.5,
            px: 0.75,
            py: 0.1,
            fontFamily: FONT_STACK,
          }}
        >
          EN
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: 13.5,
          color: "#6b7280",
          fontFamily: FONT_STACK,
          mt: 0.5,
        }}
      >
        žádá o propojení Vašich bankovních účtů
      </Typography>
    </Box>
  );
}

function CsasCard({
  children,
  maxWidth = 420,
}: {
  children: ReactNode;
  maxWidth?: number;
}) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: 3,
        p: 4,
        width: "100%",
        maxWidth,
        boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
        fontFamily: FONT_STACK,
      }}
    >
      {children}
    </Box>
  );
}

const csasInputSx = {
  width: "100%",
  bgcolor: "#fff",
  "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
  "& input": { py: 1.1, fontSize: 14, fontFamily: FONT_STACK },
};

/**
 * Česká spořitelna / George PSD2 flow: credentials → date of birth → George
 * mobile push approval → account consent.
 */
export function CsasFlow({
  thirdPartyName,
  ownerName,
  username,
  accounts,
  initialStep,
  onConnect,
  onCancel,
}: BankFlowProps) {
  const [stage, setStage] = useState<CsasStage>(
    initialStep === "accounts" ? "consent" : "credentials",
  );
  const [loginName, setLoginName] = useState(username);
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );

  // The George mobile push approval "arrives" by itself after a moment, like
  // the real in-app confirmation would.
  useEffect(() => {
    if (stage !== "george") return;
    const timer = setTimeout(() => setStage("consent"), 3000);
    return () => clearTimeout(timer);
  }, [stage]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const primaryButtonSx = {
    bgcolor: CSAS_BUTTON,
    color: "#fff",
    borderRadius: 1.5,
    fontFamily: FONT_STACK,
    fontWeight: 700,
    textTransform: "none",
    py: 1.1,
    boxShadow: "none",
    "&:hover": { bgcolor: "#175C95", boxShadow: "none" },
  };

  const cancelLinkSx = {
    color: "#D32F2F",
    fontWeight: 700,
    fontSize: 14,
    fontFamily: FONT_STACK,
    textDecoration: "none",
    cursor: "pointer",
  };

  const firstAccount = accounts[0];
  const extraCount = Math.max(accounts.length - 1, 0);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: CSAS_BG,
        fontFamily: FONT_STACK,
        position: "relative",
        px: { xs: 3, md: 8 },
        py: 4,
      }}
    >
      <Box sx={{ position: "absolute", top: 32, left: 32 }}>
        <CsasWordmark />
      </Box>

      {/* Card centred horizontally and vertically in the viewport. */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        {stage === "credentials" && (
          <>
            <CsasCard>
              <CsasThirdPartyHeader thirdPartyName={thirdPartyName} />

              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: CSAS_TEXT,
                  fontFamily: FONT_STACK,
                  mb: 0.75,
                }}
              >
                Klientské číslo / Uživatelské jméno
              </Typography>
              <TextField
                value={loginName}
                onChange={(event) => setLoginName(event.target.value)}
                sx={csasInputSx}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Typography sx={{ fontSize: 18, color: "#9aa0a6" }}>
                        ⌨
                      </Typography>
                    ),
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={() => setStage("birth")}
                disabled={!loginName}
                sx={{ ...primaryButtonSx, mt: 2.5 }}
              >
                Pokračovat
              </Button>

              <Divider sx={{ my: 2.5, fontSize: 12, color: "#9aa0a6" }}>
                Nebo
              </Divider>

              <Button
                fullWidth
                variant="outlined"
                sx={{
                  color: CSAS_BUTTON,
                  borderColor: "#c9d3dd",
                  borderRadius: 1.5,
                  fontFamily: FONT_STACK,
                  fontWeight: 700,
                  textTransform: "none",
                  py: 1.1,
                  "&:hover": { borderColor: CSAS_BUTTON, bgcolor: "#f3f7fb" },
                }}
              >
                Použít certifikát
              </Button>

              <Box sx={{ textAlign: "center", mt: 2.5 }}>
                <Link
                  sx={{
                    color: CSAS_BLUE,
                    fontSize: 14,
                    fontFamily: FONT_STACK,
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                >
                  Nepamatuji si uživatelské jméno
                </Link>
              </Box>
            </CsasCard>

            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                bgcolor: CSAS_WARNING_BG,
                border: `1px solid ${CSAS_WARNING_BORDER}`,
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13.5,
                  color: "#5c4b14",
                  fontFamily: FONT_STACK,
                }}
              >
                <strong>⚠ Pozor na tento typ podvodu!</strong> Povolíte vzdálený
                přístup a útočník může změnit, co na webu vidíte.{" "}
                <Link
                  sx={{
                    color: CSAS_BLUE,
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  Přečtěte si více.
                </Link>
              </Typography>
            </Box>
          </>
        )}

        {stage === "birth" && (
          <CsasCard>
            <CsasThirdPartyHeader thirdPartyName={thirdPartyName} />

            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: CSAS_TEXT,
                fontFamily: FONT_STACK,
                mb: 2,
              }}
            >
              Zadejte den a měsíc narození
            </Typography>

            <Stack direction="row" sx={{ gap: 1.5 }}>
              <TextField
                label="Den"
                value={birthDay}
                onChange={(event) => setBirthDay(event.target.value)}
                sx={{ ...csasInputSx, width: 120 }}
              />
              <Select
                displayEmpty
                value={birthMonth}
                onChange={(event) => setBirthMonth(event.target.value)}
                renderValue={(value) => (value ? value : "Měsíc")}
                sx={{
                  flex: 1,
                  bgcolor: "#fff",
                  borderRadius: 1.5,
                  fontSize: 14,
                  fontFamily: FONT_STACK,
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: 1.5 },
                }}
              >
                {MONTHS.map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            <Stack
              direction="row"
              onClick={() => setRememberDevice((prev) => !prev)}
              sx={{ alignItems: "center", gap: 1, mt: 2, cursor: "pointer" }}
            >
              <Checkbox
                size="small"
                checked={rememberDevice}
                sx={{
                  p: 0,
                  color: CSAS_BLUE,
                  "&.Mui-checked": { color: CSAS_BLUE },
                }}
              />
              <Typography
                sx={{ fontSize: 14, color: CSAS_TEXT, fontFamily: FONT_STACK }}
              >
                Zapamatovat pro toto zařízení
              </Typography>
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={() => setStage("george")}
              sx={{ ...primaryButtonSx, mt: 3 }}
            >
              Pokračovat
            </Button>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Box component="span" onClick={onCancel} sx={cancelLinkSx}>
                Zrušit
              </Box>
            </Box>
          </CsasCard>
        )}

        {stage === "george" && (
          <CsasCard>
            <CsasThirdPartyHeader thirdPartyName={thirdPartyName} />

            <Stack sx={{ alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: 3,
                  bgcolor: CSAS_BLUE,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 44,
                }}
              >
                📱
              </Box>

              <Typography
                sx={{
                  fontSize: 15,
                  color: CSAS_TEXT,
                  fontFamily: FONT_STACK,
                  textAlign: "center",
                }}
              >
                Pro potvrzení přejděte do George v mobilu.
              </Typography>

              <Stack sx={{ gap: 0.25, alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#6b7280",
                    fontFamily: FONT_STACK,
                  }}
                >
                  Datum a čas: 09.06.2026 12:59:36
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#6b7280",
                    fontFamily: FONT_STACK,
                  }}
                >
                  ID přihlášení: E4EJ-3352
                </Typography>
              </Stack>

              <Box component="span" onClick={onCancel} sx={cancelLinkSx}>
                Zrušit
              </Box>
            </Stack>
          </CsasCard>
        )}

        {stage === "consent" && (
          <CsasCard maxWidth={480}>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
                color: CSAS_TEXT,
                fontFamily: FONT_STACK,
                mb: 1.5,
              }}
            >
              Souhlas s předáním údajů
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                color: CSAS_TEXT,
                fontFamily: FONT_STACK,
                mb: 2.5,
              }}
            >
              Potvrzujete, že služba <strong>{thirdPartyName}</strong> získá
              přístup k Vašim údajům a pověřujete Českou spořitelnu a.s., aby
              předala této službě informace o Vašich produktech.
            </Typography>

            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: CSAS_TEXT,
                fontFamily: FONT_STACK,
                mb: 0.25,
              }}
            >
              Přístup k bankovním účtům
            </Typography>
            {firstAccount && (
              <Typography
                sx={{
                  fontSize: 13.5,
                  color: "#6b7280",
                  fontFamily: FONT_STACK,
                  mb: 1.5,
                }}
              >
                {firstAccount.accountNumber}
                {extraCount > 0 ? ` (a ${extraCount} další)` : ""}
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: CSAS_TEXT,
                fontFamily: FONT_STACK,
                mb: 1,
              }}
            >
              {ownerName}
            </Typography>
            <Box
              data-tour="bank-accounts"
              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
              {accounts.map((account) => (
                <Stack
                  key={account.id}
                  direction="row"
                  onClick={() => toggle(account.id)}
                  sx={{ alignItems: "center", gap: 1, cursor: "pointer" }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(account.id)}
                    sx={{
                      p: 0,
                      color: CSAS_BLUE,
                      "&.Mui-checked": { color: CSAS_BLUE },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: CSAS_TEXT,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    {account.accountNumber} - {account.name}
                  </Typography>
                </Stack>
              ))}
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: CSAS_TEXT,
                fontFamily: FONT_STACK,
                mb: 1,
              }}
            >
              Povinné údaje
            </Typography>
            <Stack sx={{ gap: 0.75 }}>
              <Link
                sx={{
                  color: CSAS_BLUE,
                  fontSize: 14,
                  fontFamily: FONT_STACK,
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                Možnost zadat platbu v rozsahu směrnice PSD2
              </Link>
              <Link
                sx={{
                  color: CSAS_BLUE,
                  fontSize: 14,
                  fontFamily: FONT_STACK,
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                Přístup k informacím o účtech v rozsahu směrnice PSD2
              </Link>
            </Stack>

            <Button
              data-tour="bank-connect-submit"
              fullWidth
              variant="contained"
              disabled={selectedIds.size === 0}
              onClick={() =>
                onConnect(
                  accounts.filter((account) => selectedIds.has(account.id)),
                )
              }
              sx={{ ...primaryButtonSx, mt: 3 }}
            >
              Potvrdit
            </Button>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Box component="span" onClick={onCancel} sx={cancelLinkSx}>
                Zrušit
              </Box>
            </Box>

            <Stack sx={{ gap: 0.5, mt: 3 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#9aa0a6",
                  fontFamily: FONT_STACK,
                }}
              >
                Zásady zpracování osobních údajů v České spořitelně.
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#9aa0a6",
                  fontFamily: FONT_STACK,
                }}
              >
                Přístup k údajům můžete kdykoliv zrušit v aplikaci Správa třetích
                stran ve svém internetovém bankovnictví.
              </Typography>
            </Stack>
          </CsasCard>
        )}
      </Box>
    </Box>
  );
}
